import { useState, Suspense } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { authService } from "@shared/services";
import { AdminHeader } from "@shared/features/admin/AdminHeader";
import { AdminContainer } from "@shared/features/admin/AdminContainer";
import { AdminNav } from "@shared/features/admin/AdminNav";
import { THEME } from "@shared/constants/designSystem";
import { useSiteGradients } from "@shared/hooks";
import PageLoader from "@shared/components/feedback/PageLoader";
import AdminErrorBoundary from "@shared/components/feedback/AdminErrorBoundary";

export const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // Get gradients from the hook
  const gradients = useSiteGradients();

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className={`flex h-screen overflow-hidden ${THEME.COLORS.BG.DEFAULT}`}>
      {/* Desktop Navigation Sidebar with gradient */}
      <div
        className={`
          fixed inset-y-0 left-0 w-64 flex-shrink-0 
          border-r border-gray-200 dark:border-gray-700 
          ${gradients.layoutSidebar || "bg-gray-100 dark:bg-gray-900"} 
          transform transition-transform duration-300 ease-in-out 
          ${isMobileNavOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static
          z-30 md:z-auto
        `}
      >
        <div className="w-full flex flex-col h-full">
          <div
            className={`
            h-16 flex items-center px-6 
            border-gray-200 dark:border-gray-700 border-b ${gradients.primaryMedium || "bg-primary-600 dark:bg-primary-700"}
          `}
          >
            <h1 className={`text-xl font-bold ${THEME.COLORS.TEXT.DEFAULT}`}>
              Admin Panel
            </h1>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <AdminNav />
          </div>
        </div>
      </div>

      {/* Mobile backdrop */}
      {isMobileNavOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setIsMobileNavOpen(false)}
        />
      )}

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col min-w-0 overflow-hidden ${THEME.COLORS.BG.DEFAULT}`}
      >
        <AdminHeader
          onLogout={handleLogout}
          onMenuClick={() => setIsMobileNavOpen(true)}
        />
        <main className="flex-1 overflow-y-auto">
          <AdminContainer>
            <AdminErrorBoundary>
              <Suspense fallback={<PageLoader />}>{children}</Suspense>
            </AdminErrorBoundary>
          </AdminContainer>
        </main>
      </div>
    </div>
  );
};

AdminLayout.propTypes = {
  children: PropTypes.node.isRequired,
};
